import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography } from "@mui/material";
import PageContainer from "../layouts/PageContainer";
import AppCircularProgress from "../components/common/AppCircularProgress";
import DocumentHeaderCard from "../components/documentHeaders/DocumentHeaderCard";
import ViewDocumentHeaderModal from "../components/documentHeaders/ViewDocumentHeaderModal";
import UpdateDocumentHeaderModal from "../components/documentHeaders/UpdateDocumentHeaderModal";
import {
  getDocumentHeaders,
  resetDocumentHeaderServerStatus,
} from "../redux/actions/documentHeaderActions";
import { usePermissions } from "../hooks/usePermissions";
import PageError from "../components/common/PageError";

const DocumentHeadersPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    documentHeaders,
    loadingDocumentHeaders,
    errorLoadingDocumentHeaders,
    serverStatus,
    loadingDocumentHeaderUpdate,
    errorUpdatingDocumentHeader,
  } = useSelector((state) => state.documentHeader);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(null);

  const canUpdate = hasPermission("document_header:update");

  const fetchHeaders = useCallback(() => {
    dispatch(getDocumentHeaders());
  }, [dispatch]);

  useEffect(() => {
    fetchHeaders();
  }, [fetchHeaders]);

  const handleOpenView = (header) => {
    setSelectedHeader(header);
    setIsViewModalOpen(true);
  };

  const handleOpenUpdate = (header) => {
    setSelectedHeader(header);
    setIsUpdateModalOpen(true);
  };

  useEffect(() => {
    if (serverStatus?.operation === "update" && serverStatus?.status === 200) {
      setIsUpdateModalOpen(false);
      setSelectedHeader(null);
      dispatch(resetDocumentHeaderServerStatus());
    }
  }, [serverStatus, dispatch]);

  const renderContent = () => {
    if (loadingDocumentHeaders) return <AppCircularProgress />;
    if (errorLoadingDocumentHeaders)
      return (
        <PageError error={errorLoadingDocumentHeaders} onClick={fetchHeaders} />
      );
    if (!documentHeaders || documentHeaders.length === 0)
      return (
        <Typography textAlign="center">No document templates found.</Typography>
      );

    return (
      <Grid container spacing={3}>
        {documentHeaders.map((header) => (
          <Grid item key={header.id} xs={12} sm={6} md={4}>
            <DocumentHeaderCard
              documentHeader={header}
              onView={() => handleOpenView(header)}
              onEdit={canUpdate ? () => handleOpenUpdate(header) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Document Templates"
      subtitle="Manage headers, footers, and company identities for printed documents."
    >
      {renderContent()}

      {isViewModalOpen && (
        <ViewDocumentHeaderModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          documentHeader={selectedHeader}
        />
      )}

      {isUpdateModalOpen && selectedHeader && (
        <UpdateDocumentHeaderModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          documentHeader={selectedHeader}
          loading={loadingDocumentHeaderUpdate}
          error={errorUpdatingDocumentHeader}
        />
      )}
    </PageContainer>
  );
};

export default DocumentHeadersPage;
